import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';

interface UsageData {
  invoiceScans: number;
  transactionsImported: number;
  apiCalls: number;
}

interface UseUsageTrackingReturn {
  usage: UsageData;
  loading: boolean;
  incrementInvoiceScans: (count?: number) => Promise<boolean>;
  incrementTransactions: (count?: number) => Promise<boolean>;
  incrementApiCalls: (count?: number) => Promise<boolean>;
  refreshUsage: () => Promise<void>;
}

const getCurrentMonthYear = (): string => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

export function useUsageTracking(): UseUsageTrackingReturn {
  const { user } = useAuth();
  const [usage, setUsage] = useState<UsageData>({
    invoiceScans: 0,
    transactionsImported: 0,
    apiCalls: 0,
  });
  const [loading, setLoading] = useState(true);

  const fetchUsage = useCallback(async () => {
    if (!user) {
      setUsage({ invoiceScans: 0, transactionsImported: 0, apiCalls: 0 });
      setLoading(false);
      return;
    }

    const monthYear = getCurrentMonthYear();
    
    const { data, error } = await supabase
      .from('usage_tracking')
      .select('invoice_scans, transactions_imported, api_calls')
      .eq('user_id', user.id)
      .eq('month_year', monthYear)
      .maybeSingle();

    if (error) {
      console.error('Error fetching usage:', error);
    }

    if (data) {
      setUsage({
        invoiceScans: data.invoice_scans,
        transactionsImported: data.transactions_imported,
        apiCalls: data.api_calls,
      });
    } else {
      setUsage({ invoiceScans: 0, transactionsImported: 0, apiCalls: 0 });
    }
    
    setLoading(false);
  }, [user]);

  useEffect(() => {
    fetchUsage();
  }, [fetchUsage]);

  const ensureUsageRecord = async (): Promise<boolean> => {
    if (!user) return false;
    
    const monthYear = getCurrentMonthYear();
    
    // Try to get existing record
    const { data: existing } = await supabase
      .from('usage_tracking')
      .select('id')
      .eq('user_id', user.id)
      .eq('month_year', monthYear)
      .maybeSingle();

    if (!existing) {
      // Create new record for this month
      const { error } = await supabase
        .from('usage_tracking')
        .insert({
          user_id: user.id,
          month_year: monthYear,
          invoice_scans: 0,
          transactions_imported: 0,
          api_calls: 0,
        });

      if (error) {
        console.error('Error creating usage record:', error);
        return false;
      }
    }
    
    return true;
  };

  const incrementInvoiceScans = async (count: number = 1): Promise<boolean> => {
    if (!user) return false;
    
    await ensureUsageRecord();
    const monthYear = getCurrentMonthYear();

    const { error } = await supabase
      .from('usage_tracking')
      .update({ invoice_scans: usage.invoiceScans + count })
      .eq('user_id', user.id)
      .eq('month_year', monthYear);

    if (error) {
      console.error('Error incrementing invoice scans:', error);
      return false;
    }

    setUsage(prev => ({ ...prev, invoiceScans: prev.invoiceScans + count }));
    return true;
  };

  const incrementTransactions = async (count: number = 1): Promise<boolean> => {
    if (!user) return false;
    
    await ensureUsageRecord();
    const monthYear = getCurrentMonthYear();

    const { error } = await supabase
      .from('usage_tracking')
      .update({ transactions_imported: usage.transactionsImported + count })
      .eq('user_id', user.id)
      .eq('month_year', monthYear);

    if (error) {
      console.error('Error incrementing transactions:', error);
      return false;
    }

    setUsage(prev => ({ ...prev, transactionsImported: prev.transactionsImported + count }));
    return true;
  };

  const incrementApiCalls = async (count: number = 1): Promise<boolean> => {
    if (!user) return false;
    
    await ensureUsageRecord();
    const monthYear = getCurrentMonthYear();

    const { error } = await supabase
      .from('usage_tracking')
      .update({ api_calls: usage.apiCalls + count })
      .eq('user_id', user.id)
      .eq('month_year', monthYear);

    if (error) {
      console.error('Error incrementing API calls:', error);
      return false;
    }

    setUsage(prev => ({ ...prev, apiCalls: prev.apiCalls + count }));
    return true;
  };

  return {
    usage,
    loading,
    incrementInvoiceScans,
    incrementTransactions,
    incrementApiCalls,
    refreshUsage: fetchUsage,
  };
}
